import { useMemo, useState } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { EventWithUserDataResponse, ServiceDomainResponse } from '@/openapi/schema';

import { SERVICE_DOMAIN_KEY } from '../filters/ServiceDomainFilter';
import { CLASSIFICATION_FILTER_KEY } from '../filters/TypeFilter';

const eventOptions = ['all', 'DOMAIN', 'EXPERIENCE', 'SYSTEM'].map((key) => ({
  selectedKey: key,
  content: [key],
}));

export function useEventFilters(
  events: EventWithUserDataResponse[] | undefined,
  serviceDomains: ServiceDomainResponse[] | undefined,
) {
  const { getSelectedKeysFromURL } = useUrlFilters();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);

  const serviceDomainIdsFromApi = useMemo(() => {
    if (!events) return [];
    return [...new Set(events.flatMap((api) => api.serviceDomain?.id))];
  }, [events]);

  const serviceDomainOptions = useMemo(() => {
    if (!serviceDomains) return [];
    return serviceDomains
      .map((sd) => ({
        content: [sd.serviceDomainId, sd.name],
        selectedKey: sd.id,
        selected_value: sd.name,
      }))
      .filter((opt) => serviceDomainIdsFromApi.includes(opt.selectedKey));
  }, [serviceDomains, serviceDomainIdsFromApi]);

  const typeFilters = useMemo(
    () => getSelectedKeysFromURL(CLASSIFICATION_FILTER_KEY, eventOptions),
    [getSelectedKeysFromURL],
  );

  const serviceDomainFilter = useMemo(
    () => getSelectedKeysFromURL(SERVICE_DOMAIN_KEY, serviceDomainOptions),
    [getSelectedKeysFromURL, serviceDomainOptions],
  );

  const filteredEvents = useMemo(() => {
    const lowerCaseQuery = debouncedQuery.toLowerCase();
    if (!events) return [];

    return events
      .filter((event) => {
        const matchesFilter =
          typeFilters.length === 0 || typeFilters.includes('all') || typeFilters.includes(event.classification);
        const matchesQuery = [event.name, event.slug].some(
          (field) => field && field.toLowerCase().includes(lowerCaseQuery),
        );
        const matchesDomain =
          serviceDomainFilter.length === 0 || serviceDomainFilter.includes(event.serviceDomain?.id ?? '');
        return matchesFilter && matchesQuery && matchesDomain;
      })
      .sort((e1, e2) => (e1.isFavorite === e2.isFavorite ? 0 : e1.isFavorite ? -1 : 1));
  }, [debouncedQuery, events, typeFilters, serviceDomainFilter]);

  return {
    query,
    setQuery,
    filteredEvents,
    serviceDomainOptions,
    serviceDomainFilter,
    eventOptions,
    eventFilters: typeFilters,
  };
}
